import { DialogActionsBar } from "@tql/ui-components-react/kendo/dialogs";
import { RadioButton } from "@tql/ui-components-react/kendo/inputs";
import { Label } from "@tql/ui-components-react/kendo/labels";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { clsx } from "clsx";
import { useCallback, useEffect, useState } from "react";

import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import { useIsMobile } from "~/shared/hooks/use-is-mobile.ts";
import type {
	PaymentNonce,
	SavedAch,
	SavedCard,
} from "~/shared/types/payment-types.ts";

import { PaymentErrorModal } from "../../error-modal/index.tsx";
import { useGetBraintreeToken } from "../../hooks/use-get-braintree-token.ts";
import { useGetCustomerAccounts } from "../../hooks/use-get-customer-accounts.ts";
import { INITIAL_NEW_PAYMENT_VALUES } from "../utils/constants.ts";
import { DisplaySavedPaymentMethods } from "./components/display-saved-payment-methods/display-saved-payment-methods.tsx";
import { NewPaymentMethodForm } from "./components/new-payment-method-form/new-payment-method-form.tsx";
import { useBraintree } from "./hooks/use-braintree.ts";
import { usePaymentMethodFormValidation } from "./hooks/use-payment-method-form-validation.ts";
import { useSavePaymentMethod } from "./hooks/use-save-payment-method.ts";
import styles from "./index.module.css";

type PaymentMethodModalProps = {
	availablePaymentMethods?:
		| {
				paymentMethodID: number;
				paymentMethodName: string;
		  }[]
		| null;
	onClose: () => void;
	handleApplyPaymentMethod: (
		paymentMethod: SavedCard | SavedAch | PaymentNonce | null,
	) => void;
	selectedPaymentValue: SavedCard | SavedAch | PaymentNonce | null;
};

export const PaymentMethodModal = ({
	onClose,
	handleApplyPaymentMethod,
	availablePaymentMethods,
	selectedPaymentValue,
}: PaymentMethodModalProps) => {
	const [showForm, setShowForm] = useState(false);
	const [selectedMethod, setSelectedMethod] = useState<
		"ACH" | "CreditCard" | null
	>("ACH");
	const [stagedPaymentMethod, setStagedPaymentMethod] = useState<
		SavedCard | SavedAch | PaymentNonce | null
	>(selectedPaymentValue);

	const { accounts, isLoading: isLoadingAccounts } = useGetCustomerAccounts();
	const isMobile = useIsMobile();

	const [newPaymentValues, setNewPaymentValues] = useState(
		INITIAL_NEW_PAYMENT_VALUES,
	);

	useEffect(() => {
		setStagedPaymentMethod(selectedPaymentValue);
	}, [selectedPaymentValue]);

	// If the staged payment method has been deleted (no longer in accounts), clear it
	useEffect(() => {
		if (!stagedPaymentMethod || !accounts?.paymentAccounts) return;
		const stagedToken = stagedPaymentMethod.paymentMethodToken;
		if (!stagedToken) return;
		const stillExists = accounts.paymentAccounts.some(
			(a) => a.paymentMethodToken === stagedToken,
		);
		if (!stillExists) {
			setStagedPaymentMethod(null);
		}
	}, [accounts, stagedPaymentMethod]);

	// Auto-select default payment method when switching types
	useEffect(() => {
		if (accounts?.paymentAccounts && !showForm) {
			const filteredAccounts = accounts.paymentAccounts.filter(
				(a) => a.paymentMethod === selectedMethod,
			);
			const defaultAccount = filteredAccounts.find((a) => a.isDefault);

			// Only update if we don't have a staged payment method for this type yet
			if (
				defaultAccount &&
				(!stagedPaymentMethod ||
					stagedPaymentMethod.paymentMethod !== selectedMethod)
			) {
				setStagedPaymentMethod(defaultAccount);
			}
		}
	}, [selectedMethod, accounts, showForm, stagedPaymentMethod]);

	const { token: braintreeToken } = useGetBraintreeToken();
	const { tokenizeCard, tokenizeAch, hostedFieldsValidity, hostedFieldsEmpty } =
		useBraintree(braintreeToken, showForm, selectedMethod);

	const [showValidation, setShowValidation] = useState(false);

	const { isFormValid, errors } = usePaymentMethodFormValidation({
		newPaymentValues,
		selectedMethod,
		hostedFieldsValidity,
		hostedFieldsEmpty,
		showValidation,
	});

	const handleSetNewPaymentValue = useCallback(
		(field: string, value: string | boolean) => {
			setNewPaymentValues((prevValues) => ({
				...prevValues,
				[field]: value,
			}));
		},
		[],
	);

	const {
		handleSavePaymentMethod,
		isSaving,
		brainTreeError,
		savePaymentMethodError,
		resetErrors,
	} = useSavePaymentMethod({
		selectedMethod,
		newPaymentValues,
		tokenizeCard,
		tokenizeAch,
		handleApplyPaymentMethod,
		setShowForm,
		onClose,
	});

	const handleCancelClick = () => {
		setShowForm(false);
		setShowValidation(false);
	};

	const getButtonLabel = () => {
		if (!showForm) {
			return "Add New Payment Method";
		}
		return isSaving ? "Saving..." : "Add Payment Method";
	};

	return (
		<>
			<PaymentErrorModal
				errorMessage={
					(brainTreeError || savePaymentMethodError?.message) ??
					"An error occurred while saving the payment method. Please try again."
				}
				isOpen={!!brainTreeError || !!savePaymentMethodError}
				onClose={resetErrors}
			/>
			<TraxModal
				className={clsx(
					styles.paymentMethodModal,
					isMobile && styles.paymentMethodModalMobile,
				)}
				onClose={onClose}
				title={showForm ? "Add New Payment Method" : "Add Payment Method"}
				width={isMobile ? undefined : "40%"}
			>
				<div className={styles.sectionSpacing}>
					<div className={styles.paymentMethodOptions}>
						{availablePaymentMethods?.map((method) => (
							<div
								key={`payment-method-${method.paymentMethodID}`}
								className={styles.paymentMethodOption}
							>
								<RadioButton
									checked={selectedMethod === method.paymentMethodName}
									onChange={() => {
										const methodName = method.paymentMethodName as
											| "ACH"
											| "CreditCard";
										setSelectedMethod(methodName);
									}}
								/>
								<Label>
									{method.paymentMethodName.includes("ACH")
										? "ACH"
										: "Credit Card"}
								</Label>
							</div>
						))}
					</div>
					<hr />
				</div>
				<div className={styles.sectionSpacing}>
					{showForm ? (
						<NewPaymentMethodForm
							errors={errors}
							handleSetNewPaymentValue={handleSetNewPaymentValue}
							newPaymentValues={newPaymentValues}
							paymentType={selectedMethod}
						/>
					) : (
						<DisplaySavedPaymentMethods
							accounts={accounts}
							handleApplyPaymentMethod={setStagedPaymentMethod}
							isLoadingAccounts={isLoadingAccounts}
							selectedMethod={selectedMethod}
							selectedPayment={stagedPaymentMethod}
						/>
					)}
				</div>
				<div
					className={clsx(
						styles.formActions,
						showForm ? styles.formActionsEnd : styles.formActionsStart,
					)}
				>
					{showForm ? (
						<Button onClick={handleCancelClick}>Cancel</Button>
					) : null}
					<Button
						disabled={isSaving}
						onClick={() => {
							if (showForm) {
								setShowValidation(true);
								if (isFormValid) {
									handleSavePaymentMethod();
								}
							} else {
								setShowForm(true);
							}
						}}
						variant={"secondary"}
					>
						{getButtonLabel()}
					</Button>
				</div>
				{showForm ? null : (
					<DialogActionsBar>
						<div className={"trax-modal-confirmation-actions"}>
							<Button onClick={onClose} variant={"secondary"}>
								Close
							</Button>
							<Button
								disabled={stagedPaymentMethod === null}
								onClick={() => {
									if (stagedPaymentMethod) {
										handleApplyPaymentMethod(stagedPaymentMethod);
										onClose();
									}
								}}
							>
								Apply Payment Method
							</Button>
						</div>
					</DialogActionsBar>
				)}
			</TraxModal>
		</>
	);
};
